import React, { useState } from 'react';
import { Select, theme, Popover } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';

const selectPopupProps = (token) => ({
  classNames: { popup: { root: 'context-selector-dropdown' } },
  popupMatchSelectWidth: false,
  styles: {
    popup: {
      root: {
        minWidth: 220,
        padding: 6,
        borderRadius: token.borderRadiusLG,
        overflow: 'hidden',
      },
    },
  },
});

function ContextSelectField({
  label,
  value,
  options,
  onChange,
  disabled,
  fieldRef,
  popupProps,
  tipMessage = null,
}) {
  const [open, setOpen] = useState(false);

  const handleChevronMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  const field = (
    <div className="context-selector-field" ref={fieldRef}>
      <div className="context-selector-field-body">
        <span className="context-selector-field-label">{label}</span>
        <Select
          className="context-selector-select"
          value={value}
          onChange={(selectedValue) => {
            onChange(selectedValue);
            setOpen(false);
          }}
          options={options}
          disabled={disabled}
          open={open}
          onOpenChange={setOpen}
          suffixIcon={
            <span
              className={`context-selector-chevron-btn${open ? ' is-open' : ''}`}
              onMouseDown={handleChevronMouseDown}
              role="button"
              tabIndex={-1}
              aria-label={`Open ${label} dropdown`}
            >
              <CaretDownOutlined className="context-selector-chevron" />
            </span>
          }
          variant="borderless"
          showSearch
          optionFilterProp="label"
          popupMatchSelectWidth={false}
          {...popupProps}
        />
      </div>
    </div>
  );

  return (
    <Popover
      open={Boolean(tipMessage)}
      placement="bottom"
      arrow
      destroyOnHidden
      classNames={{ root: "context-selector-tip" }}
      content={<span className="context-selector-tip-text">{tipMessage}</span>}
      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
    >
      {field}
    </Popover>
  );
}

export default function ContextSelector({
  groupRef,
  companyRef,
  groupValue,
  companyValue,
  groups,
  companys,
  isDataLoaded,
  onGroupSelect,
  onCompanySelect,
  variant = 'inline',
  showSelectionTips = true,
}) {
  const { token } = theme.useToken();
  const popupProps = selectPopupProps(token);
  const loadingLabel = 'Loading...';
  const resolvedGroupValue = isDataLoaded ? groupValue : loadingLabel;
  const resolvedCompanyValue = isDataLoaded ? companyValue : loadingLabel;
  const groupTip =
    showSelectionTips && isDataLoaded && groupValue === 'Select Group'
      ? 'Please select your Group.'
      : null;
  const companyTip =
    showSelectionTips &&
    !groupTip &&
    isDataLoaded &&
    companyValue === 'Select Company'
      ? 'Please select your Company.'
      : null;

  const containerStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    backgroundColor: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
    ['--context-selector-divider']: token.colorBorderSecondary,
    ['--context-selector-border-hover']: token.colorPrimary,
    ['--context-selector-text']: token.colorText,
    ['--context-selector-label']: token.colorTextTertiary,
    ['--context-selector-chevron']: token.colorTextSecondary,
    ['--context-selector-chevron-active']: token.colorPrimary,
    ['--context-selector-chevron-bg']: token.colorFillQuaternary,
    ['--context-selector-chevron-bg-hover']: token.colorFillTertiary,
  };

  return (
    <div
      className={`context-selector context-selector--${variant}`}
      style={containerStyle}
    >
      <ContextSelectField
        fieldRef={groupRef}
        label="Group"
        value={resolvedGroupValue}
        options={groups}
        onChange={onGroupSelect}
        disabled={!isDataLoaded}
        popupProps={popupProps}
        tipMessage={groupTip}
      />
      <span className="context-selector-divider" aria-hidden="true" />
      <ContextSelectField
        fieldRef={companyRef}
        label="Company"
        value={resolvedCompanyValue}
        options={companys}
        onChange={onCompanySelect}
        disabled={!isDataLoaded}
        popupProps={popupProps}
        tipMessage={companyTip}
      />
    </div>
  );
}
